import { defineComponent as _defineComponent } from 'vue'
import { JobStatus } from '@/@types/enums/JobStatus';
import EmptyStateCard from '@/components/layout/cards/EmptyStateCard.vue';
import MyTable from '@/components/layout/MyTable/MyTable.vue';
import MyTableCell from '@/components/layout/MyTable/MyTableCell.vue';
import InlineIcon from '@/components/layout/shared/InlineIcon.vue';
import { Job } from '@/models/Job';
import { Logger } from '@/other/Logger';
import apiService from '@/services/ApiService';
import confirmationService from '@/services/ConfirmationService';
import { Loading } from 'element-ui';
import { debounce } from 'lodash';
import moment from 'moment';
import { computed, onMounted, ref } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'AdminJobs',
  setup(__props) {

const log = new Logger('AdminJobs');

const jobs = ref<Job[]>([]);
const jobsCount = ref<number>(0);

const tableColumns = computed(() => {
  return [
    { label: 'NAME', sortBy: ['name'] },
    { label: 'ATTEMPTS', sortBy: ['attempts'] },
    { label: 'LAST ATTEMPTS', sortBy: ['nextAttemptAt'] },
    { label: 'STATUS', sortBy: ['status'] },
    { label: 'PARAMS' },
    { label: '' },
  ];
});

const searchQuery = ref<string>('');

const isErrorAlertVisible = ref<boolean>(false);
const isLogAlertVisible = ref<boolean>(false);
const errorAlertContent = ref<string>('');
const logAlertContent = ref<string>('');

// Infinite scroll variables
const isLoadingMoreJobs = ref<boolean>(true);
const isPageLoading = ref<boolean>(false);
const currentPage = ref<number>(1);
const PAGE_LIMIT = ref<number>(30);

const isJobStatusProcessing = computed((job: Job) => {
  return job.status === JobStatus.PROCESSING;
});

const getJobWorkerName = computed((job: Job) => {
  return job.workerName ? job.workerName : 'Application';
});

const getJobs = computed(() => jobs.value);

const getTimeFromLateUpdate = computed((job: Job) => {
  const now = Date.now();
  const ms = moment(now).diff(moment(job.updatedAt));
  const d = moment.duration(ms);
  return Math.floor(d.asHours()) + moment.utc(ms).format(':mm:ss');
});

const formatDate = (timestamp: number) => {
  return moment(timestamp).format('HH:mm DD.MM.YYYY');
};

const handleClickRefresh = () => {
  load();
  loadJobsCount();
};

const handleClickRemoveDone = async () => {
  try {
    await apiService.admin.deleteDoneJobs();
    await loadJobsCount();
    await load();
  } catch (err) {
    apiService.handleResponseError(err);
  }
};

const handleClickShowError = (item: Job) => {
  isErrorAlertVisible.value = true;
  errorAlertContent.value = item.error;
};

const handleClickShowLog = (item: Job) => {
  isLogAlertVisible.value = true;
  logAlertContent.value = '<pre>' + item.log + '</pre>';
};

const handleClickDelete = async (item: Job) => {
  const success = await confirmationService
    .confirm('Are you sure that you want to delete this job?', 'Are you sure?', {
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
      type: 'info',
    })
    .catch((error) => {
      log.error(error);
      return null;
    });

  if (success === null) return;
  const index = jobs.value.indexOf(item);

  if (index >= 0) {
    await apiService.admin.deleteJob(item.id);
    jobsCount.value = jobsCount.value - 1;
    jobs.value.splice(index, 1);
  }
};

const handleClickReset = async (item: Job) => {
  const success = await confirmationService
    .confirm('Are you sure that you want to reset this job?', 'Are you sure?', {
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
      type: 'info',
    })
    .catch((error) => {
      log.error(error);
      return null;
    });

  if (success === null) return;
  const index = jobs.value.indexOf(item);

  if (index >= 0) {
    item.attempts = 0;
    await apiService.admin.resetJob(item.id);
  }
};

const load = async () => {
  const loader = Loading.service({ lock: true });

  currentPage.value = 1;
  isLoadingMoreJobs.value = true;

  try {
    const res = await apiService.admin.getJobs({
      q: searchQuery.value.length ? searchQuery.value : undefined,
      page: 1,
    });
    const resJobs = res.data.jobs;
    jobs.value.splice(0);
    resJobs.forEach((item: any) => {
      jobs.value.push(Job.parse(item));
    });
  } catch (err) {
    apiService.handleResponseError(err);
  } finally {
    loader.close();
    loader.$destroy();
  }
};

const loadJobsCount = async () => {
  try {
    const res = await apiService.admin.getJobsCount();
    const count = res.data.count;
    jobsCount.value = count;
  } catch (err) {
    apiService.handleResponseError(err);
  }
};

const scrollFetchCompanies = async (page = 1) => {
  try {
    const res = await apiService.admin.getJobs({
      q: searchQuery.value.length ? searchQuery.value : undefined,
      page,
    });

    return res.data.jobs;
  } catch (err) {
    apiService.handleResponseError(err);
  }
};

const handleScroll = async (event: any) => {
  const target = event.target as HTMLElement;
  const distanceToBottom = target.scrollHeight - (target.scrollTop + target.clientHeight);

  const threshold = 200;

  if (distanceToBottom > threshold || !isLoadingMoreJobs.value) return;

  isLoadingMoreJobs.value = false;
  currentPage.value += 1;

  try {
    isPageLoading.value = true;
    const jobs = await scrollFetchCompanies(currentPage.value);

    if (jobs) {
      jobs.forEach((item: any) => {
        jobs.value.push(Job.parse(item));
      });
      isLoadingMoreJobs.value = jobs.length >= PAGE_LIMIT.value;
    } else {
      isLoadingMoreJobs.value = false;
    }
  } finally {
    isPageLoading.value = false;
  }
};

const handleSearchInput = debounce(async () => {
  currentPage.value = 1;

  await load();
}, 500);

onMounted(async () => {
  await loadJobsCount();

  await load();
});

return { __sfc: true,log, jobs, jobsCount, tableColumns, searchQuery, isErrorAlertVisible, isLogAlertVisible, errorAlertContent, logAlertContent, isLoadingMoreJobs, isPageLoading, currentPage, PAGE_LIMIT, isJobStatusProcessing, getJobWorkerName, getJobs, getTimeFromLateUpdate, formatDate, handleClickRefresh, handleClickRemoveDone, handleClickShowError, handleClickShowLog, handleClickDelete, handleClickReset, load, loadJobsCount, scrollFetchCompanies, handleScroll, handleSearchInput, EmptyStateCard, MyTable, MyTableCell, InlineIcon }
}

})